/*
 * The six systems that make up Adam. Each one is a WebGL scene with a
 * small HUD that names what the visual is doing. Events and numbers
 * are simulated for illustration.
 */
import G from './gl';

export function initScenes(container = document) {
  const canvases = [...container.querySelectorAll("canvas[data-scene]")];

  const markFallback = canvas => canvas.closest(".part-visual").classList.add("no-webgl");

  const { THREE, palette, rand } = G;
  const V3 = THREE.Vector3;
  const ORIGIN = new V3();

  const format = n => Math.round(n).toLocaleString("en-US");

  function hudOf(canvas) {
    const root = canvas.closest(".part-visual");
    return {
      root,
      get: name => root.querySelector(`[data-hud="${name}"]`),
      label(html, color) {
        const el = document.createElement("div");
        el.className = "scene-label";
        el.innerHTML = `<span>${html}</span>`;
        if (color) el.style.setProperty("--c", color);
        root.querySelector(".scene-labels").append(el);
        return el;
      }
    };
  }

  /*
   * Runs fn every `interval` seconds of scene time, so events pause
   * together with the scene when it scrolls out of view.
   */
  function every(interval, fn) {
    let elapsed = 0;
    return dt => {
      elapsed += dt;
      while (elapsed >= interval) {
        elapsed -= interval;
        fn();
      }
    };
  }

  /*
   * Fade a HUD element out, change it, fade it back in.
   */
  function swap(el, change) {
    if (!el) return;
    if (G.reducedMotion) {
      change(el);
      return;
    }
    el.classList.add("out");
    setTimeout(() => {
      change(el);
      el.classList.remove("out");
    }, 260);
  }

  const css = color => "#" + color.getHexString();

  /* ------------------------------------------------------------------
   * 01 · BRAIN
   * A brain-shaped cloud of memories grouped by what they are about.
   * New memories fly in and wire themselves to the nearest hub.
   * ------------------------------------------------------------------ */
  function brainScene(canvas) {
    const stage = G.createStage(canvas, { z: 6.6, fit: 1.4 });
    if (!stage) return false;

    const hud = hudOf(canvas);
    const root = new THREE.Group();
    stage.scene.add(root);

    const categories = [
      { name: "Persona", color: palette.cyan, center: new V3(-1.3, 0.55, 0.4) },
      { name: "Signals", color: palette.amber, center: new V3(1.35, 0.6, -0.1) },
      { name: "Objections", color: palette.violet, center: new V3(-0.95, -0.7, -0.6) },
      { name: "Playbook", color: palette.blue, center: new V3(1.05, -0.65, 0.6) },
      { name: "Voice", color: palette.pink, center: new V3(0, 0.45, -1.05) }
    ];

    const lessons = [
      [4, "Drop exclamation marks — they read as automated"],
      [1, "Funding news → replies ×2.1 within 72 hours"],
      [2, "“We already use an agency” → reframe on ownership"],
      [3, "Two-slot close beats an open question (+18%)"],
      [0, "Ops leaders prefer numbers over adjectives"],
      [2, "“Not now” in Q4 usually means January"],
      [4, "Mirror the prospect's message length"],
      [1, "New SDR job posts = pipeline pain"],
      [3, "Follow-up #2 works best with a new fact"],
      [0, "Founders answer before 9am, VPs after 4pm"]
    ];

    function sampleBrain(out) {
      do {
        out.set(rand(-1, 1), rand(-1, 1), rand(-1, 1));
      } while (out.lengthSq() > 1 || (Math.abs(out.x) < 0.07 && out.y > -0.3));

      out.x += Math.sign(out.x) * 0.07;
      out.y -= out.x * out.x * 0.12;
      return out.multiply(new V3(2.45, 1.55, 1.8));
    }

    function nearestCategory(p) {
      let best = 0;
      let bestDistance = Infinity;
      categories.forEach((category, i) => {
        const d = p.distanceToSquared(category.center);
        if (d < bestDistance) {
          bestDistance = d;
          best = i;
        }
      });
      return best;
    }

    const BASE = 1000;
    const POOL = 160;
    const memories = G.glowPoints(stage, BASE + POOL);
    const home = [];
    const kind = new Uint8Array(memories.count);
    const baseSize = new Float32Array(memories.count);
    const baseAlpha = new Float32Array(memories.count);
    const flash = new Float32Array(memories.count);

    for (let i = 0; i < memories.count; i++) {
      const p = sampleBrain(new V3());
      home.push(p);
      kind[i] = nearestCategory(p);
      baseSize[i] = rand(0.035, 0.075);
      baseAlpha[i] = i < BASE ? rand(0.35, 0.9) : 0;
      memories.setPosition(i, p);
      memories.setColor(i, categories[kind[i]].color, rand(0.6, 1));
    }

    root.add(memories.points);

    /*
     * Hubs: larger nodes wired together like synapses.
     */
    const hubs = [];
    for (let i = 0; i < BASE; i += 13) hubs.push(i);
    hubs.forEach(i => { baseSize[i] = 0.12; baseAlpha[i] = 1; });

    const edges = [];
    hubs.forEach((a, ai) => {
      hubs
        .map(b => ({ b, d: home[a].distanceTo(home[b]) }))
        .filter(item => item.b !== a)
        .sort((x, y) => x.d - y.d)
        .slice(0, 3)
        .forEach(item => {
          if (hubs.indexOf(item.b) > ai) edges.push([a, item.b]);
        });
    });

    const synapses = G.glowLines(edges.length);
    edges.forEach(([a, b], i) => {
      synapses.setSegment(i, home[a], home[b], categories[kind[a]].color, 0.28, categories[kind[b]].color, 0.28);
    });
    synapses.flush(edges.length);
    root.add(synapses.lines);

    const PULSES = 70;
    const pulses = G.glowPoints(stage, PULSES);
    const pulseState = Array.from({ length: PULSES }, () => ({
      edge: edges[Math.floor(Math.random() * edges.length)],
      t: Math.random(),
      speed: rand(0.35, 0.9)
    }));
    for (let i = 0; i < PULSES; i++) {
      pulses.size[i] = 0.08;
      pulses.setColor(i, palette.white);
    }
    root.add(pulses.points);

    /*
     * New memories arrive from outside and wire into the nearest hub.
     */
    const wiring = G.glowLines(POOL);
    const wireLevel = new Float32Array(POOL);
    const wireHub = new Int32Array(POOL).fill(-1);
    root.add(wiring.lines);

    const flights = [];
    let spawned = 0;
    let lessonIndex = 0;
    let memoryTotal = 18406;

    const memoriesEl = hud.get("memories");
    const latestEl = hud.get("latest");

    function spawn() {
      const [category, text] = lessons[lessonIndex++ % lessons.length];
      const slot = BASE + (spawned++ % POOL);

      home[slot].copy(categories[category].center).add(G.randomInSphere(0.55, new V3()));
      kind[slot] = category;
      memories.setColor(slot, categories[category].color);
      baseAlpha[slot] = 0;

      let nearestHub = hubs[0];
      hubs.forEach(h => {
        if (home[h].distanceToSquared(home[slot]) < home[nearestHub].distanceToSquared(home[slot])) {
          nearestHub = h;
        }
      });
      wireHub[slot - BASE] = nearestHub;
      wireLevel[slot - BASE] = 0;

      flights.push({
        slot,
        from: home[slot].clone().normalize().multiplyScalar(5.5).add(new V3(0, rand(-1, 1), 0)),
        t: 0
      });

      swap(latestEl, el => {
        const c = categories[category];
        el.innerHTML = `<span class="hud-tag" style="--c:${css(c.color)}">${c.name}</span>${text}`;
      });
    }

    const labels = categories.map(c => ({
      el: hud.label(c.name, css(c.color)),
      anchor: c.center.clone().add(new V3(0, 0.75, 0))
    }));

    const spawnTick = every(1.7, spawn);
    const p = new V3();

    stage.start((dt, time) => {
      root.rotation.y = time * 0.09 + stage.pointer.x * 0.45;
      root.rotation.x = -0.1 + stage.pointer.y * 0.25;
      root.updateMatrixWorld();

      spawnTick(dt);

      for (let f = flights.length - 1; f >= 0; f--) {
        const flight = flights[f];
        flight.t += dt / 1.3;
        const t = G.ease(Math.min(1, flight.t));
        p.copy(flight.from).lerp(home[flight.slot], t);
        memories.setPosition(flight.slot, p);
        memories.alpha[flight.slot] = 0.4 + t * 0.6;
        memories.size[flight.slot] = 0.12;

        if (flight.t >= 1) {
          flights.splice(f, 1);
          baseAlpha[flight.slot] = 0.95;
          baseSize[flight.slot] = 0.07;
          flash[flight.slot] = 1;
          flash[wireHub[flight.slot - BASE]] = 1;
          wireLevel[flight.slot - BASE] = 1;
          memoryTotal += 1 + Math.floor(Math.random() * 3);
          if (memoriesEl) memoriesEl.textContent = format(memoryTotal);
        }
      }

      for (let i = 0; i < memories.count; i++) {
        if (flash[i] > 0) flash[i] = Math.max(0, flash[i] - dt * 0.9);
        if (i >= BASE && flights.some(flight => flight.slot === i)) continue;
        memories.size[i] = baseSize[i] * (1 + flash[i] * 3);
        memories.alpha[i] = baseAlpha[i] * (0.8 + Math.sin(time * 1.4 + i) * 0.2) + flash[i] * 0.5;
      }
      memories.flush();

      for (let w = 0; w < POOL; w++) {
        const hub = wireHub[w];
        if (hub < 0) continue;
        const resting = baseAlpha[BASE + w] > 0 ? 0.18 : 0;
        wireLevel[w] = Math.max(resting, wireLevel[w] - dt * 0.6);
        wiring.setSegment(w, home[BASE + w], home[hub], categories[kind[BASE + w]].color, wireLevel[w] + resting);
      }
      wiring.flush();

      pulseState.forEach((pulse, i) => {
        pulse.t += dt * pulse.speed;
        if (pulse.t >= 1) {
          pulse.t = 0;
          pulse.edge = edges[Math.floor(Math.random() * edges.length)];
        }
        p.copy(home[pulse.edge[0]]).lerp(home[pulse.edge[1]], pulse.t);
        pulses.setPosition(i, p);
        pulses.alpha[i] = Math.sin(pulse.t * Math.PI) * 0.9;
      });
      pulses.flush();

      labels.forEach(label => {
        const depth = G.placeLabel(stage, label.el, root, label.anchor);
        label.el.classList.toggle("back", depth < -0.4);
      });
    });

    spawn();
    return true;
  }

  /* ------------------------------------------------------------------
   * 02 · SIGNAL DETECTION
   * Thousands of sources on a sphere, swept by a crawler. Relevant
   * signals travel to Adam and land in the next message; noise dies out.
   * ------------------------------------------------------------------ */
  function signalsScene(canvas) {
    const stage = G.createStage(canvas, { z: 11.2, fit: 1.5 });
    if (!stage) return false;

    const hud = hudOf(canvas);
    const root = new THREE.Group();
    stage.scene.add(root);

    const signals = [
      { src: "techcrunch.com", headline: "NovaTech closes €12M Series A", line: "congrats on closing the Series A.", relevant: true },
      { src: "novatech.io/careers", headline: "3 SDR roles posted this week", line: "saw you're hiring three SDRs.", relevant: true },
      { src: "local-events.com", headline: "NovaTech sponsors a charity 10k", relevant: false },
      { src: "linkedin.com", headline: "Alex posts about pipeline gaps", line: "your post on pipeline gaps stuck with me.", relevant: true },
      { src: "g2.com", headline: "Their current tool: 2.1★ on support", line: "if support has been the sore spot lately,", relevant: true },
      { src: "property-weekly.com", headline: "NovaTech moves office floors", relevant: false }
    ];

    const SOURCES = 1600;
    const R = 3.3;
    const sources = G.glowPoints(stage, SOURCES);
    const sourcePos = G.fibonacciSphere(SOURCES, R).map(s => s.multiplyScalar(rand(0.97, 1.03)));
    const fire = new Float32Array(SOURCES);
    const fireColor = Array(SOURCES).fill(palette.amber);

    sourcePos.forEach((s, i) => {
      sources.setPosition(i, s);
      sources.size[i] = rand(0.035, 0.06);
    });
    root.add(sources.points);

    // The crawler sweep: a great circle that rotates around the sphere.
    const sweep = new THREE.Group();
    const sweepRing = G.circle(R * 1.02, 0x62f4e5, 0.35, 200);
    sweep.add(sweepRing);
    root.add(sweep);

    // Adam
    const core = G.glowPoints(stage, 260);
    for (let i = 0; i < core.count; i++) {
      core.setPosition(i, G.randomInSphere(0.5, new V3()));
      core.setColor(i, i % 3 ? palette.cyan : palette.white);
      core.size[i] = rand(0.04, 0.08);
      core.alpha[i] = rand(0.5, 1);
    }
    core.flush();
    root.add(core.points);

    const coreHalo = G.glowPoints(stage, 1);
    coreHalo.size[0] = 2.2;
    coreHalo.setColor(0, palette.cyan);
    root.add(coreHalo.points);

    const coreShell = G.wireframe(new THREE.IcosahedronGeometry(0.8, 1), 0x62f4e5, 0.2);
    root.add(coreShell);

    // Signal arcs
    const ARCS = 5;
    const STEPS = 48;
    const arcLines = G.glowLines(ARCS * (STEPS - 1));
    const heads = G.glowPoints(stage, ARCS);
    root.add(arcLines.lines, heads.points);

    const arcs = Array.from({ length: ARCS }, () => ({
      active: false,
      t: 0,
      life: 1,
      signal: null,
      reported: false,
      curve: Array.from({ length: STEPS }, () => new V3())
    }));

    let next = 0;
    let watched = 4812;
    let coreBoost = 0;

    const feedEl = hud.get("feed");
    const draftEl = hud.get("draft");
    const sourcesEl = hud.get("sources");

    const sweepNormal = new V3();
    const control = new V3();

    function launch() {
      const arc = arcs.find(a => !a.active);
      if (!arc) return;

      let index = 0;
      for (let tries = 0; tries < 60; tries++) {
        index = Math.floor(Math.random() * SOURCES);
        if (Math.abs(sourcePos[index].dot(sweepNormal)) < 0.35 && sourcePos[index].z > -1) break;
      }

      const signal = signals[next++ % signals.length];
      const from = sourcePos[index];
      control.copy(from).multiplyScalar(0.5).add(G.randomOnSphere(1.3, new V3()));

      arc.curve.forEach((point, k) => G.bezier(point, from, control, ORIGIN, k / (STEPS - 1)));
      arc.active = true;
      arc.t = 0;
      arc.life = 1;
      arc.signal = signal;
      arc.reported = false;

      fire[index] = 1;
      fireColor[index] = signal.relevant ? palette.amber : palette.slate;
    }

    function report(signal) {
      const item = document.createElement("li");
      item.className = signal.relevant ? "used" : "skipped";
      item.innerHTML = `<b>${signal.src}</b><span>${signal.headline}</span><em>${signal.relevant ? "→ used in the next message" : "skipped · not relevant"}</em>`;
      feedEl.prepend(item);
      while (feedEl.children.length > 4) feedEl.lastElementChild.remove();

      if (signal.relevant) {
        swap(draftEl, el => {
          el.innerHTML = `Hi Alex — <mark>${signal.line}</mark> Teams scaling that fast usually find pipeline can't keep up with headcount. Worth 20 minutes?`;
        });
      }

      watched += Math.floor(rand(0, 3));
      if (sourcesEl) sourcesEl.textContent = format(watched);
    }

    const launchTick = every(1.6, launch);
    const p = new V3();

    stage.start((dt, time) => {
      // Leave room for the feed panel, which sits beside the scene on wide screens.
      root.position.x = stage.width > 760 ? -1.4 : 0;
      root.rotation.y = time * 0.05 + stage.pointer.x * 0.35;
      root.rotation.x = 0.15 + stage.pointer.y * 0.2;

      sweep.rotation.y = time * 0.55;
      sweep.rotation.x = 0.35;
      sweep.updateMatrix();
      sweepNormal.set(0, 0, 1).applyQuaternion(sweep.quaternion);

      launchTick(dt);

      for (let i = 0; i < SOURCES; i++) {
        const near = Math.max(0, 1 - Math.abs(sourcePos[i].dot(sweepNormal)) / 0.35);
        if (fire[i] > 0) fire[i] = Math.max(0, fire[i] - dt * 0.35);
        const f = fire[i];
        sources.setColor(i, f > 0.02 ? fireColor[i] : palette.blue, f > 0.02 ? 1 : 0.8 + near * 0.5);
        sources.alpha[i] = 0.4 + near * 0.5 + f;
        sources.size[i] = 0.045 * (1 + f * 3 + near * 0.4);
      }
      sources.flush();

      let segment = 0;
      arcs.forEach((arc, a) => {
        if (!arc.active) {
          heads.alpha[a] = 0;
          return;
        }

        arc.t += dt * 0.5;
        const stopAt = arc.signal.relevant ? 1 : 0.55;

        if (arc.t >= stopAt) {
          if (!arc.reported) {
            arc.reported = true;
            report(arc.signal);
            if (arc.signal.relevant) coreBoost = 1;
          }
          arc.life -= dt * 1.4;
          if (arc.life <= 0) arc.active = false;
        }

        const head = Math.min(arc.t, stopAt) * (STEPS - 1);
        const color = arc.signal.relevant ? palette.amber : palette.slate;

        for (let k = 0; k < STEPS - 1; k++) {
          const trail = k < head ? Math.max(0, 1 - (head - k) / 22) : 0;
          arcLines.setSegment(segment++, arc.curve[k], arc.curve[k + 1], color, trail * Math.max(0, arc.life));
        }

        p.copy(arc.curve[Math.min(STEPS - 1, Math.floor(head))]);
        heads.setPosition(a, p);
        heads.setColor(a, color);
        heads.size[a] = 0.16;
        heads.alpha[a] = arc.t < stopAt ? 1 : Math.max(0, arc.life);
      });

      for (; segment < arcLines.max; segment++) arcLines.setSegment(segment, ORIGIN, ORIGIN, palette.amber, 0);
      arcLines.flush();
      heads.flush();

      coreBoost = Math.max(0, coreBoost - dt * 1.2);
      core.points.scale.setScalar(1 + coreBoost * 0.35);
      core.points.rotation.y = time * 0.4;
      coreHalo.alpha[0] = 0.15 + coreBoost * 0.35;
      coreHalo.flush();
      coreShell.rotation.y = -time * 0.3;
      coreShell.material.opacity = 0.18 + coreBoost * 0.4;
    });

    return true;
  }

  /* ------------------------------------------------------------------
   * 03 · AUTO-IMPROVEMENT
   * Replies and your own edits fly into Adam. Common patterns are
   * absorbed and reshape it; outliers are set aside on their own orbit.
   * ------------------------------------------------------------------ */
  function improveScene(canvas) {
    const stage = G.createStage(canvas, { z: 9.4, fit: 1.4 });
    if (!stage) return false;

    const hud = hudOf(canvas);
    const root = new THREE.Group();
    stage.scene.add(root);

    const RADIUS = 1.25;
    const directions = G.fibonacciSphere(1300, 1);
    const core = G.glowPoints(stage, directions.length);
    directions.forEach((d, i) => {
      core.size[i] = rand(0.04, 0.065);
      core.alpha[i] = rand(0.55, 0.95);
    });
    root.add(core.points);

    const ripples = [];
    let roughness = 0.16;
    let youTint = 0;

    const YOU = new V3(-0.85, 0.32, 0.4).normalize().multiplyScalar(3.9);

    const outlierOrbit = new THREE.Group();
    outlierOrbit.rotation.set(1.2, 0.2, 0.35);
    outlierOrbit.add(G.circle(3.05, 0xff8fc7, 0.22));
    root.add(outlierOrbit);
    const orbitInverse = outlierOrbit.quaternion.clone().invert();
    const tint = new THREE.Color();

    const kinds = {
      common: {
        color: palette.cyan,
        lines: [
          "CFO asks for numbers → common (61%) → lead with ROI",
          "“Send me a deck” → common → shorter deck follow-up",
          "Reply within 4 min → interest pattern → same-day follow-up",
          "“Who else uses it?” → common → proof earlier in thread"
        ]
      },
      outlier: {
        color: palette.pink,
        lines: [
          "“Call me in 2028” → outlier → set aside, no rule change",
          "Out-of-office in Finnish → outlier → ignored",
          "One-off angry reply → outlier → logged, persona untouched"
        ]
      },
      you: {
        color: palette.amber,
        lines: [
          "You rewrote the opener → tone 12% less formal",
          "You added a case study → proof-first weight +0.06",
          "You signed off with first name only → adopted"
        ]
      }
    };

    const PARTICLES = 40;
    const particles = G.glowPoints(stage, PARTICLES);
    const items = Array.from({ length: PARTICLES }, () => ({
      active: false,
      kind: "common",
      pos: new V3(),
      dir: new V3(),
      phase: "in",
      angle: 0,
      t: 0,
      born: 0
    }));
    root.add(particles.points);

    const counts = { analysed: 12840, reinforced: 9312, isolated: 1206 };
    let styleMatch = 91.4;
    const lineIndex = { common: 0, outlier: 0, you: 0 };

    const tickerEl = hud.get("ticker");
    const styleBar = hud.get("style-bar");
    const styleEl = hud.get("style");

    function renderCounts() {
      ["analysed", "reinforced", "isolated"].forEach(key => {
        const el = hud.get(key);
        if (el) el.textContent = format(counts[key]);
      });
      if (styleEl) styleEl.textContent = styleMatch.toFixed(1) + "%";
      if (styleBar) styleBar.style.setProperty("--v", styleMatch + "%");
    }

    function announce(kind) {
      const lines = kinds[kind].lines;
      const text = lines[lineIndex[kind]++ % lines.length];
      const label = { common: "pattern", outlier: "outlier", you: "your edit" }[kind];
      swap(tickerEl, el => {
        el.innerHTML = `<span class="hud-tag" style="--c:${css(kinds[kind].color)}">${label}</span>${text}`;
      });
    }

    let spawnCount = 0;

    function spawn() {
      const roll = Math.random();
      const kind = spawnCount++ % 5 === 2 ? "you" : roll < 0.28 ? "outlier" : "common";

      let item = items.find(it => !it.active);
      if (!item) {
        item = items
          .filter(it => it.phase === "orbit")
          .sort((a, b) => a.born - b.born)[0];
        if (!item) return;
      }

      item.active = true;
      item.kind = kind;
      item.phase = "in";
      item.born = performance.now();
      item.t = 0;

      if (kind === "you") {
        item.pos.copy(YOU).add(G.randomInSphere(0.25, new V3()));
      } else {
        G.randomOnSphere(4.8, item.pos);
        item.pos.y *= 0.6;
      }

      item.dir.copy(item.pos).negate().normalize();
    }

    const youBeacon = G.glowPoints(stage, 1);
    youBeacon.setPosition(0, YOU);
    youBeacon.setColor(0, palette.amber);
    youBeacon.size[0] = 0.5;
    youBeacon.alpha[0] = 0.5;
    youBeacon.flush();
    root.add(youBeacon.points);

    const youLabel = hud.label("You", css(palette.amber));
    const orbitLabel = hud.label("Outliers · set aside", css(palette.pink));
    const orbitAnchor = new V3(3.05, 0, 0);

    const spawnTick = every(0.45, spawn);
    const p = new V3();
    const tmp = new V3();

    function orbitPoint(angle, out) {
      out.set(Math.cos(angle) * 3.05, Math.sin(angle) * 3.05, 0);
      return out.applyEuler(outlierOrbit.rotation);
    }

    stage.start((dt, time) => {
      root.rotation.y = Math.sin(time * 0.1) * 0.3 + stage.pointer.x * 0.35;
      root.rotation.x = stage.pointer.y * 0.2;
      root.updateMatrixWorld();

      spawnTick(dt);

      // Entropy creeps back in, every reinforced pattern smooths it again.
      roughness = Math.min(0.2, roughness + dt * 0.006);
      youTint = Math.max(0, youTint - dt * 0.5);

      for (let r = ripples.length - 1; r >= 0; r--) {
        ripples[r].age += dt;
        if (ripples[r].age > 2.5) ripples.splice(r, 1);
      }

      for (let i = 0; i < directions.length; i++) {
        const d = directions[i];
        let disp = roughness * Math.sin(d.x * 3.1 + time * 0.9) * Math.sin(d.y * 3.7 + time * 1.1) * Math.sin(d.z * 2.9 + time * 0.7);

        let rippleGlow = 0;
        let rippleColor = null;
        for (const ripple of ripples) {
          const angle = Math.acos(Math.max(-1, Math.min(1, d.dot(ripple.dir))));
          const front = angle - ripple.age * 1.8;
          const wave = Math.exp(-front * front * 14) * Math.exp(-ripple.age * 1.3);
          disp += wave * 0.22;
          if (wave > rippleGlow) {
            rippleGlow = wave;
            rippleColor = ripple.color;
          }
        }

        p.copy(d).multiplyScalar(RADIUS + disp);
        core.setPosition(i, p);

        tint.copy(palette.violet).lerp(palette.cyan, G.clamp01(0.5 + disp * 4));
        if (youTint > 0) tint.lerp(palette.amber, youTint * 0.5);
        if (rippleColor) tint.lerp(rippleColor, Math.min(1, rippleGlow * 1.5));
        core.setColor(i, tint);
      }
      core.flush();

      items.forEach((item, i) => {
        if (!item.active) {
          particles.alpha[i] = 0;
          return;
        }

        const color = kinds[item.kind].color;
        particles.setColor(i, color);
        particles.size[i] = item.kind === "you" ? 0.28 : 0.2;
        particles.alpha[i] = 1;

        if (item.phase === "in") {
          item.pos.addScaledVector(item.dir, dt * 1.9);
          const r = item.pos.length();

          if (item.kind === "outlier" && r < 2.1) {
            item.phase = "deflect";
            item.t = 0;
            item.from = item.pos.clone();
            tmp.copy(item.pos).applyQuaternion(orbitInverse);
            item.angle = Math.atan2(tmp.y, tmp.x);
            counts.analysed++;
            counts.isolated++;
            renderCounts();
            announce("outlier");
          } else if (r < RADIUS + 0.05) {
            item.active = false;
            ripples.push({ dir: item.pos.clone().normalize(), age: 0, color });
            counts.analysed++;
            if (item.kind === "you") {
              youTint = 1;
              styleMatch = Math.min(98.9, styleMatch + 0.3);
            } else {
              counts.reinforced++;
              roughness = Math.max(0.04, roughness - 0.03);
            }
            renderCounts();
            announce(item.kind);
          }
        } else if (item.phase === "deflect") {
          item.t += dt / 1.1;
          orbitPoint(item.angle + item.t * 0.5, p);
          item.pos.copy(item.from).lerp(p, G.ease(Math.min(1, item.t)));
          if (item.t >= 1) {
            item.phase = "orbit";
            item.angle += 0.5;
          }
        } else {
          item.angle += dt * 0.22;
          orbitPoint(item.angle, item.pos);
          particles.size[i] = 0.1;
          particles.alpha[i] = 0.75;
        }

        particles.setPosition(i, item.pos);
      });
      particles.flush();

      G.placeLabel(stage, youLabel, root, tmp.copy(YOU).add(new V3(0, 0.5, 0)));
      orbitPoint(-0.5, orbitAnchor);
      G.placeLabel(stage, orbitLabel, root, orbitAnchor);
    });

    renderCounts();
    return true;
  }

  /* ------------------------------------------------------------------
   * 04 · MULTIPLE HUNTERS
   * An army of agents, each assigned to a customer segment and channel,
   * hunting in parallel and reporting back to a shared core.
   * ------------------------------------------------------------------ */
  function huntersScene(canvas) {
    const stage = G.createStage(canvas, { z: 8.2, y: 3, fit: 1.5 });
    if (!stage) return false;

    const hud = hudOf(canvas);
    const root = new THREE.Group();
    stage.scene.add(root);

    const segments = [
      { name: "SaaS founders", channel: "LinkedIn", color: palette.cyan },
      { name: "Enterprise CTOs", channel: "Email", color: palette.blue },
      { name: "Agencies", channel: "LinkedIn", color: palette.violet },
      { name: "E-commerce", channel: "X", color: palette.amber },
      { name: "Logistics ops", channel: "Email", color: palette.pink }
    ];

    // A faint command table under everything.
    [1.4, 2.6, 3.9].forEach(r => {
      const ring = G.circle(r, 0x718cff, 0.1);
      ring.rotation.x = Math.PI / 2;
      root.add(ring);
    });

    const TARGETS = 80;
    const targets = G.glowPoints(stage, segments.length * TARGETS);
    const targetPos = [];
    const booked = new Uint8Array(targets.count);
    const targetFlash = new Float32Array(targets.count);

    segments.forEach((segment, s) => {
      const angle = s / segments.length * Math.PI * 2 + 0.4;
      segment.center = new V3(Math.cos(angle) * 3.3, 0.15, Math.sin(angle) * 3.3);
      segment.booked = Math.floor(rand(18, 44));

      for (let k = 0; k < TARGETS; k++) {
        const i = s * TARGETS + k;
        const offset = G.randomInSphere(0.8, new V3());
        offset.y *= 0.45;
        targetPos[i] = segment.center.clone().add(offset);
        targets.setPosition(i, targetPos[i]);
      }
    });
    root.add(targets.points);

    const core = G.glowPoints(stage, 240);
    for (let i = 0; i < core.count; i++) {
      core.setPosition(i, G.randomInSphere(0.5, new V3()));
      core.setColor(i, i % 2 ? palette.cyan : palette.violet);
      core.size[i] = rand(0.04, 0.08);
      core.alpha[i] = rand(0.5, 1);
    }
    core.flush();
    root.add(core.points);

    const HUNTERS = 10;
    const TRAIL = 26;
    const heads = G.glowPoints(stage, HUNTERS);
    const trails = G.glowLines(HUNTERS * (TRAIL - 1));
    root.add(trails.lines, heads.points);

    const hunters = Array.from({ length: HUNTERS }, (_, h) => ({
      segment: h % segments.length,
      phase: "rest",
      t: rand(0, 1),
      duration: 0.5,
      from: new V3(),
      control: new V3(),
      to: new V3(),
      target: -1,
      pos: new V3(),
      trail: Array.from({ length: TRAIL }, () => new V3())
    }));

    const rosterEl = hud.get("roster");
    const totalEl = hud.get("total");

    rosterEl.innerHTML = segments.map((segment, s) => `
      <li data-segment="${s}" style="--c:${css(segment.color)}">
        <i></i><strong>${segment.name}</strong><b data-count>${segment.booked}</b>
        <small><span data-status>hunting</span> · ${segment.channel}</small>
      </li>`).join("");

    const rows = [...rosterEl.children];

    function renderTotal() {
      totalEl.textContent = segments.reduce((sum, s) => sum + s.booked, 0);
    }

    function setStatus(s, text, hit) {
      const row = rows[s];
      row.querySelector("[data-status]").textContent = text;
      row.classList.toggle("hit", Boolean(hit));
      if (hit) row.querySelector("[data-count]").textContent = segments[s].booked;
    }

    function pickTarget(s) {
      const start = s * TARGETS;
      let free = [];
      for (let i = start; i < start + TARGETS; i++) if (!booked[i]) free.push(i);
      if (free.length < 10) {
        booked.fill(0, start, start + TARGETS);
        free = Array.from({ length: TARGETS }, (_, k) => start + k);
      }
      return free[Math.floor(Math.random() * free.length)];
    }

    function plan(hunter, phase) {
      hunter.phase = phase;
      hunter.t = 0;
      hunter.from.copy(hunter.pos);

      if (phase === "out") {
        hunter.target = pickTarget(hunter.segment);
        hunter.to.copy(targetPos[hunter.target]);
        hunter.duration = rand(1.5, 2.3);
        hunter.control.copy(hunter.from).lerp(hunter.to, 0.5).add(new V3(rand(-0.8, 0.8), rand(0.8, 1.6), rand(-0.8, 0.8)));
        setStatus(hunter.segment, "hunting");
      } else if (phase === "engage") {
        hunter.duration = 1.2;
        targetFlash[hunter.target] = 1;
        setStatus(hunter.segment, "in conversation");
      } else if (phase === "back") {
        hunter.to.set(0, 0, 0);
        hunter.duration = rand(1.2, 1.6);
        hunter.control.copy(hunter.from).multiplyScalar(0.5).add(new V3(0, rand(0.6, 1.2), 0));
      } else {
        hunter.duration = rand(0.2, 0.6);
      }
    }

    hunters.forEach(hunter => hunter.trail.forEach(point => point.copy(hunter.pos)));

    const labels = segments.map(segment => ({
      el: hud.label(`${segment.name} <small>${segment.channel}</small>`, css(segment.color)),
      anchor: segment.center.clone().add(new V3(0, 0.95, 0))
    }));

    let coreBoost = 0;
    const p = new V3();

    stage.start((dt, time) => {
      root.position.x = stage.width > 760 ? -1.3 : 0;
      root.rotation.y = time * 0.05 + stage.pointer.x * 0.3;
      root.updateMatrixWorld();

      hunters.forEach((hunter, h) => {
        hunter.t += dt / hunter.duration;

        if (hunter.phase === "out" || hunter.phase === "back") {
          G.bezier(hunter.pos, hunter.from, hunter.control, hunter.to, G.ease(Math.min(1, hunter.t)));
        } else if (hunter.phase === "engage") {
          const a = hunter.t * Math.PI * 4;
          hunter.pos.copy(targetPos[hunter.target]).add(p.set(Math.cos(a) * 0.2, 0.05, Math.sin(a) * 0.2));
        }

        if (hunter.t >= 1) {
          if (hunter.phase === "rest") plan(hunter, "out");
          else if (hunter.phase === "out") plan(hunter, "engage");
          else if (hunter.phase === "engage") {
            if (Math.random() < 0.55) {
              booked[hunter.target] = 1;
              targetFlash[hunter.target] = 1.5;
              segments[hunter.segment].booked++;
              setStatus(hunter.segment, "meeting booked ✓", true);
              renderTotal();
            } else {
              setStatus(hunter.segment, "nurturing");
            }
            plan(hunter, "back");
          } else if (hunter.phase === "back") {
            coreBoost = 1;
            plan(hunter, "rest");
          }
        }

        for (let k = TRAIL - 1; k > 0; k--) hunter.trail[k].copy(hunter.trail[k - 1]);
        hunter.trail[0].copy(hunter.pos);

        const color = segments[hunter.segment].color;
        heads.setPosition(h, hunter.pos);
        heads.setColor(h, color);
        heads.size[h] = hunter.phase === "engage" ? 0.22 : 0.17;
        heads.alpha[h] = hunter.phase === "rest" ? 0.4 : 1;

        for (let k = 0; k < TRAIL - 1; k++) {
          trails.setSegment(h * (TRAIL - 1) + k, hunter.trail[k], hunter.trail[k + 1], color, (1 - k / TRAIL) * 0.8);
        }
      });
      heads.flush();
      trails.flush();

      for (let i = 0; i < targets.count; i++) {
        const segment = segments[Math.floor(i / TARGETS)];
        targetFlash[i] = Math.max(0, targetFlash[i] - dt * 0.8);
        const f = targetFlash[i];
        targets.setColor(i, segment.color, booked[i] ? 1 : 0.7);
        targets.size[i] = booked[i] ? 0.12 + f * 0.2 : 0.05 + f * 0.25;
        targets.alpha[i] = booked[i] ? 0.95 : 0.5 + f * 0.5;
      }
      targets.flush();

      coreBoost = Math.max(0, coreBoost - dt * 1.5);
      core.points.scale.setScalar(1 + coreBoost * 0.25 + Math.sin(time * 2) * 0.03);
      core.points.rotation.y = time * 0.5;

      labels.forEach(label => {
        const depth = G.placeLabel(stage, label.el, root, label.anchor);
        label.el.classList.toggle("back", depth < -1.5);
      });
    });

    renderTotal();
    return true;
  }

  /* ------------------------------------------------------------------
   * 05 · AUTOMATIONS
   * A pipeline of gates that conversations flow through. Each sales
   * motion reshapes the flow: pace, volume and which stages run.
   * ------------------------------------------------------------------ */
  function automationsScene(canvas) {
    const stage = G.createStage(canvas, { z: 9, y: 1.5, fit: 1.9 });
    if (!stage) return false;

    const hud = hudOf(canvas);
    const root = new THREE.Group();
    root.position.y = 0.35;
    stage.scene.add(root);

    const stages = ["Find", "Research", "Reach out", "Converse", "Book", "Report"];
    const colors = [palette.blue, palette.blue, palette.cyan, palette.cyan, palette.violet, palette.amber, palette.white, palette.white];
    const gateX = i => -3.75 + i * 1.5;

    const modes = {
      clevel: {
        speed: 0.035, density: 0.28, spread: 0.45, active: [1, 1, 1, 1, 1, 1],
        desc: "Few, deeply researched touches. Board-level tone, three messages max, books straight into your calendar."
      },
      midmarket: {
        speed: 0.065, density: 0.6, spread: 0.7, active: [1, 1, 1, 1, 1, 1],
        desc: "Multi-threaded outreach across buying committees, with follow-ups timed to each persona."
      },
      volume: {
        speed: 0.12, density: 1, spread: 0.95, active: [1, 1, 1, 1, 1, 0.4],
        desc: "High-volume prospecting that still reads one-to-one. Every message is judged before it goes out."
      },
      support: {
        speed: 0.08, density: 0.65, spread: 0.6, active: [0.15, 0.4, 0.15, 1, 1, 1],
        desc: "Answers inbound customers in your voice, solves what it can and hands off what it can't."
      },
      reporting: {
        speed: 0.05, density: 0.5, spread: 0.55, active: [0.15, 1, 0.15, 0.15, 0.15, 1],
        desc: "Turns every conversation into account notes, CRM updates and a weekly pipeline report."
      }
    };

    const current = { speed: 0.065, density: 0.6, spread: 0.7, active: [1, 1, 1, 1, 1, 1] };
    let mode = modes.midmarket;

    const gates = stages.map((name, i) => {
      const group = new THREE.Group();
      group.position.x = gateX(i);
      group.rotation.y = Math.PI / 2;
      const outer = G.circle(1.05, 0x62f4e5, 0.5);
      const inner = G.circle(0.7, 0x718cff, 0.25);
      group.add(outer, inner);
      root.add(group);

      return {
        group,
        outer,
        inner,
        label: hud.label(`${String(i + 1).padStart(2, "0")} ${name}`, css(colors[i + 1])),
        anchor: new V3(gateX(i), -1.45, 0)
      };
    });

    const COUNT = 2200;
    const flow = G.glowPoints(stage, COUNT);
    const particle = Array.from({ length: COUNT }, () => ({
      t: Math.random(),
      angle: rand(0, Math.PI * 2),
      radius: Math.sqrt(Math.random()),
      speed: rand(0.8, 1.25),
      cut: Math.random()
    }));
    root.add(flow.points);

    const tmpColor = new THREE.Color();
    const p = new V3();

    const descEl = hud.get("mode-desc");
    const buttons = [...hud.root.querySelectorAll("[data-mode]")];
    let autoplay = true;

    function setMode(key, fromUser) {
      mode = modes[key];
      if (fromUser) autoplay = false;
      buttons.forEach(button => button.setAttribute("aria-pressed", String(button.dataset.mode === key)));
      swap(descEl, el => { el.textContent = mode.desc; });
      stage.refresh(150);
    }

    buttons.forEach(button => button.addEventListener("click", () => setMode(button.dataset.mode, true)));

    const order = Object.keys(modes);
    let orderIndex = 1;
    const autoTick = every(6.5, () => {
      if (!autoplay) return;
      orderIndex = (orderIndex + 1) % order.length;
      setMode(order[orderIndex]);
    });

    stage.start((dt, time) => {
      root.rotation.y = -0.2 + stage.pointer.x * 0.15;
      root.rotation.x = stage.pointer.y * 0.08;
      root.updateMatrixWorld();

      if (!G.reducedMotion) autoTick(dt);

      const k = Math.min(1, dt * 2.5);
      current.speed += (mode.speed - current.speed) * k;
      current.density += (mode.density - current.density) * k;
      current.spread += (mode.spread - current.spread) * k;
      current.active = current.active.map((a, i) => a + (mode.active[i] - a) * k);

      for (let i = 0; i < COUNT; i++) {
        const item = particle[i];
        item.t = (item.t + dt * current.speed * item.speed) % 1;

        const x = -4.7 + item.t * 9.4;
        let squeeze = 0;
        let glow = 0;

        for (let g = 0; g < gates.length; g++) {
          const dx = x - gateX(g);
          const near = Math.exp(-dx * dx / 0.05);
          squeeze += near * current.active[g] * 0.55;
          glow += near * current.active[g];
        }

        const r = item.radius * current.spread * (1 - squeeze) + 0.03;
        const a = item.angle + item.t * 9 + time * 0.25;
        p.set(x, Math.cos(a) * r, Math.sin(a) * r);
        flow.setPosition(i, p);

        const f = Math.max(0, Math.min(colors.length - 1.001, (x + 4.5) / 1.5));
        const index = Math.floor(f);
        tmpColor.copy(colors[index]).lerp(colors[index + 1], f - index);
        flow.setColor(i, tmpColor);

        const ends = Math.min(1, (item.t) / 0.06, (1 - item.t) / 0.06);
        flow.alpha[i] = item.cut < current.density ? ends * (0.45 + glow * 0.5) : 0;
        flow.size[i] = 0.055 + glow * 0.03;
      }
      flow.flush();

      gates.forEach((gate, i) => {
        const level = current.active[i];
        const pulse = 0.5 + 0.5 * Math.sin(time * 2.2 - i * 0.9);
        gate.outer.material.opacity = 0.08 + level * (0.35 + pulse * 0.25);
        gate.inner.material.opacity = 0.04 + level * 0.2;
        gate.inner.rotation.z = time * 0.4 * (i % 2 ? 1 : -1);
        gate.group.scale.setScalar(0.85 + level * 0.15);
        G.placeLabel(stage, gate.label, root, gate.anchor);
        gate.label.classList.toggle("off", level < 0.5);
      });
    });

    setMode("midmarket");
    return true;
  }

  /* ------------------------------------------------------------------
   * 06 · BUSINESS & PRODUCT UNDERSTANDING
   * Documents are read line by line, scored, and dissolved into the
   * brain, which rewrites the playbook from what it learned.
   * ------------------------------------------------------------------ */
  function understandingScene(canvas) {
    const stage = G.createStage(canvas, { z: 9.2, fit: 1.8 });
    if (!stage) return false;

    const hud = hudOf(canvas);
    const root = new THREE.Group();
    stage.scene.add(root);

    const STEP = 0.045;

    function layout(kind, w, h) {
      const dots = [];
      const row = (y, x0, x1) => { for (let x = x0; x <= x1; x += STEP) dots.push([x, y]); };
      const block = (x0, y0, x1, y1) => { for (let y = y0; y >= y1; y -= STEP) row(y, x0, x1); };
      const left = -w / 2 + 0.08;
      const right = w / 2 - 0.08;
      let y = h / 2 - 0.1;

      if (kind === "website") {
        row(y, left, left + 0.25);
        for (let x = right - 0.6; x <= right; x += 0.2) row(y, x, x + 0.1);
        y -= 0.2;
        block(left, y, left + w * 0.45, y - STEP * 2);
        block(right - w * 0.38, y, right, y - 0.38);
        y -= 0.2;
        for (let line = 0; line < 3; line++, y -= 0.09) row(y, left, left + w * rand(0.25, 0.45));
        y -= 0.06;
        for (let line = 0; line < 3; line++, y -= 0.09) row(y, left, right - rand(0, 0.4));
      } else if (kind === "pdf") {
        block(left, y, left + w * 0.6, y - STEP);
        y -= 0.16;
        while (y > -h / 2 + 0.08) {
          for (let line = 0; line < 4 && y > -h / 2 + 0.08; line++, y -= 0.075) {
            row(y, left, line === 3 ? left + (right - left) * rand(0.3, 0.7) : right);
          }
          y -= 0.06;
        }
      } else {
        block(left, y, left + w * 0.5, y - STEP);
        y -= 0.2;
        const base = -h / 2 + 0.1;
        [0.55, 0.35, 0.7, 0.45, 0.6].forEach((height, i) => {
          const x = left + 0.05 + i * 0.13;
          for (let yy = base; yy <= base + height * (y - base); yy += STEP) dots.push([x, yy], [x + STEP, yy]);
        });
        for (let line = 0; line < 4; line++, y -= 0.12) row(y, left + w * 0.55, right - rand(0, 0.2));
      }

      return dots;
    }

    const docs = [
      { name: "yourcompany.com", kind: "website", w: 1.55, h: 1.0, home: new V3(-3.25, 1.35, 0) },
      { name: "product-brief.pdf", kind: "pdf", w: 0.95, h: 1.2, home: new V3(-3.3, -0.05, 0) },
      { name: "sales-playbook.pptx", kind: "deck", w: 1.55, h: 0.9, home: new V3(-3.25, -1.45, 0) }
    ];

    const STATION = new V3(-0.35, 0, 0.4);
    const STATION_SCALE = 1.7;
    const CRYSTAL = new V3(2.6, 0.45, 0);

    let total = 0;
    docs.forEach(doc => {
      doc.dots = layout(doc.kind, doc.w, doc.h);
      doc.start = total;
      total += doc.dots.length;
      doc.pos = doc.home.clone();
      doc.scale = 1;
      doc.alpha = 0.55;
      doc.targets = doc.dots.map(() => CRYSTAL.clone().add(G.randomInSphere(0.95, new V3())));
      doc.controls = doc.dots.map(() => new V3(rand(0.5, 1.6), rand(-1.4, 1.4), rand(-0.6, 0.9)));
    });

    const paper = G.glowPoints(stage, total);
    root.add(paper.points);

    const outlines = G.glowLines(docs.length * 4 + 1);
    root.add(outlines.lines);

    const crystalPoints = G.fibonacciSphere(560, 1.05).map(p => p.add(CRYSTAL));
    const crystal = G.glowPoints(stage, crystalPoints.length);
    crystalPoints.forEach((p, i) => {
      crystal.setPosition(i, p);
      crystal.setColor(i, i % 3 ? palette.violet : palette.cyan);
      crystal.size[i] = rand(0.035, 0.06);
    });
    crystal.flush();
    root.add(crystal.points);

    const crystalShell = G.wireframe(new THREE.IcosahedronGeometry(1.3, 1), 0xb18cff, 0.18);
    crystalShell.position.copy(CRYSTAL);
    const crystalInner = G.wireframe(new THREE.OctahedronGeometry(0.7, 0), 0x62f4e5, 0.35);
    crystalInner.position.copy(CRYSTAL);
    root.add(crystalShell, crystalInner);

    const scores = [
      { positioning: 72, icp: 64, proof: 40, objections: 35 },
      { positioning: 81, icp: 78, proof: 58, objections: 52 },
      { positioning: 88, icp: 91, proof: 74, objections: 71 }
    ];

    const docRows = [...hud.root.querySelectorAll("[data-doc]")];
    const playbookEl = hud.get("playbook");
    let playbookVersion = 1;

    function setRow(i, state, text) {
      const row = docRows[i];
      if (!row) return;
      row.className = state;
      row.querySelector("em").textContent = text;
    }

    function setScores(values) {
      Object.entries(values).forEach(([key, value]) => {
        const el = hud.root.querySelector(`[data-score="${key}"]`);
        if (!el) return;
        el.style.setProperty("--v", value + "%");
        el.querySelector("b").textContent = value;
      });
    }

    let current = 0;
    let phase = "move";
    let phaseTime = 0;
    let crystalLevel = 0.3;
    let crystalBoost = 0;

    const durations = { move: 0.9, scan: 1.9, dissolve: 1.9, settle: 0.6, rewrite: 2, restore: 1.2 };

    function enter(next) {
      phase = next;
      phaseTime = 0;

      if (phase === "move") setRow(current, "reading", "reading…");
      if (phase === "dissolve") setRow(current, "reading", "learning…");
      if (phase === "settle") {
        setRow(current, "done", "scored ✓");
        setScores(scores[current]);
        crystalBoost = 1;
        crystalLevel = Math.min(1, crystalLevel + 0.22);
      }
      if (phase === "rewrite") {
        playbookVersion++;
        swap(playbookEl, el => { el.innerHTML = `<b>v${playbookVersion}</b> · 3 gaps fixed`; });
        crystalBoost = 1.6;
      }
      if (phase === "restore") {
        docs.forEach((doc, i) => {
          doc.pos.copy(doc.home);
          doc.scale = 1;
          setRow(i, "", "queued");
        });
        crystalLevel = 0.3;
      }
    }

    const p = new V3();
    const corner = [new V3(), new V3(), new V3(), new V3()];
    const scanA = new V3();
    const scanB = new V3();

    stage.start((dt, time) => {
      root.rotation.y = -0.12 + stage.pointer.x * 0.2;
      root.rotation.x = stage.pointer.y * 0.12;

      phaseTime += dt;
      const progress = Math.min(1, phaseTime / durations[phase]);

      if (progress >= 1) {
        if (phase === "move") enter("scan");
        else if (phase === "scan") enter("dissolve");
        else if (phase === "dissolve") enter("settle");
        else if (phase === "settle") {
          current++;
          if (current < docs.length) enter("move");
          else enter("rewrite");
        } else if (phase === "rewrite") enter("restore");
        else if (phase === "restore") {
          current = 0;
          enter("move");
        }
      }

      const active = docs[current];

      if (phase === "move" && active) {
        const t = G.ease(progress);
        active.pos.copy(active.home).lerp(STATION, t);
        active.scale = 1 + (STATION_SCALE - 1) * t;
      }

      let segment = 0;
      let scanY = Infinity;

      docs.forEach((doc, d) => {
        const gone = d < current && phase !== "restore";
        const isActive = d === current && phase !== "rewrite" && phase !== "restore";
        const dissolving = isActive && (phase === "dissolve" || phase === "settle");
        let docAlpha;

        if (phase === "restore") docAlpha = progress * 0.55;
        else if (gone || (dissolving && phase === "settle")) docAlpha = 0;
        else if (isActive) docAlpha = dissolving ? 1 - progress : 1;
        else docAlpha = 0.5;

        if (isActive && phase === "scan") scanY = doc.h / 2 - progress * doc.h;

        doc.dots.forEach(([u, v], k) => {
          const i = doc.start + k;
          const onPage = p.set(doc.pos.x + u * doc.scale, doc.pos.y + v * doc.scale, doc.pos.z);

          if (dissolving) {
            const delay = (doc.h / 2 - v) / doc.h * 0.5;
            const t = phase === "settle" ? 1 : G.clamp01((progress - delay) / 0.5);
            const control = onPage.clone().lerp(doc.targets[k], 0.5).add(doc.controls[k]);
            G.bezier(p, onPage.clone(), control, doc.targets[k], G.ease(t));
            paper.setColor(i, t > 0.6 ? palette.violet : palette.cyan);
            paper.alpha[i] = t < 1 ? 1 : 0;
            paper.size[i] = 0.075;
          } else {
            const scanned = isActive && phase === "scan" && v > scanY;
            paper.setColor(i, scanned ? palette.cyan : palette.blue, scanned ? 1 : 0.9);
            paper.alpha[i] = (gone ? 0 : 0.8 + (scanned ? 0.2 : 0)) * (phase === "restore" ? progress : 1);
            paper.size[i] = 0.042 * doc.scale;
          }

          paper.setPosition(i, p);
        });

        const hw = doc.w / 2 * doc.scale + 0.05;
        const hh = doc.h / 2 * doc.scale + 0.05;
        corner[0].set(doc.pos.x - hw, doc.pos.y + hh, doc.pos.z);
        corner[1].set(doc.pos.x + hw, doc.pos.y + hh, doc.pos.z);
        corner[2].set(doc.pos.x + hw, doc.pos.y - hh, doc.pos.z);
        corner[3].set(doc.pos.x - hw, doc.pos.y - hh, doc.pos.z);
        const lineColor = isActive ? palette.cyan : palette.blue;
        for (let c = 0; c < 4; c++) {
          outlines.setSegment(segment++, corner[c], corner[(c + 1) % 4], lineColor, docAlpha * 0.6);
        }

        if (scanY !== Infinity && isActive) {
          scanA.set(doc.pos.x - hw - 0.15, doc.pos.y + scanY * doc.scale, doc.pos.z + 0.01);
          scanB.set(doc.pos.x + hw + 0.15, doc.pos.y + scanY * doc.scale, doc.pos.z + 0.01);
        }
      });

      if (scanY !== Infinity) {
        outlines.setSegment(segment++, scanA, scanB, palette.cyan, 1.4);
      }
      outlines.flush(segment);
      paper.flush();

      crystalBoost = Math.max(0, crystalBoost - dt * 1.1);
      for (let i = 0; i < crystal.count; i++) {
        crystal.alpha[i] = (0.25 + crystalLevel * 0.6) * (0.8 + Math.sin(time * 2 + i) * 0.2) + crystalBoost * 0.3;
      }
      crystal.points.position.copy(CRYSTAL).multiplyScalar(-(crystalBoost * 0.1));
      crystal.points.scale.setScalar(1 + crystalBoost * 0.1);
      crystal.flush();

      crystalShell.rotation.y = time * 0.25;
      crystalShell.rotation.x = time * 0.1;
      crystalShell.material.opacity = 0.12 + crystalLevel * 0.15 + crystalBoost * 0.2;
      crystalInner.rotation.y = -time * 0.6;
      crystalInner.material.opacity = 0.2 + crystalBoost * 0.4;
    });

    enter("move");
    return true;
  }

  const scenes = {
    brain: brainScene,
    signals: signalsScene,
    improve: improveScene,
    hunters: huntersScene,
    automations: automationsScene,
    understanding: understandingScene
  };

  canvases.forEach(canvas => {
    const build = scenes[canvas.dataset.scene];
    if (!build || !build(canvas)) markFallback(canvas);
  });
}
